/**
 * "Sidebar tour" — quick orientation to each module in the AML platform's left
 * navigation. Some nav items are role-gated (CO/SM/GB/Admin only); steps for
 * those are silently skipped via `showOn` for users who don't have access.
 *
 * Targets used: every nav <a> already has `data-tour="nav-<key>"` injected via
 * Aml.vue's v-for, so no further markup changes are required.
 */

import { NEXT, BACK, START, SKIP, DONE, targetExists } from './_helpers.js';

const navStep = (id, navKey, title, text, on = 'right') => ({
    id,
    attachTo: { element: `[data-tour="nav-${navKey}"]`, on },
    showOn: targetExists(`[data-tour="nav-${navKey}"]`),
    title,
    text,
    buttons: [BACK, NEXT],
});

export const sidebarTour = {
    id: 'sidebar',
    title: 'Sidebar tour',
    description: 'A fast tour of every module in the left sidebar — what each one is for and when you would use it.',
    autoLaunchOn: { page: 'dashboard' },

    steps: [
        {
            id: 'intro',
            title: 'The sidebar',
            text: `The sidebar on the left is how you move between the main modules of the
                AML platform. Each one covers a distinct area of compliance work. Some
                modules are only visible to compliance officers, senior managers, or admins.`,
            buttons: [START],
        },
        navStep(
            'dashboard',
            'dashboard',
            'Dashboard',
            'Your home view. Surfaces tasks, recent activity, escalations, client risk, and the health of your AML program. Run the dedicated <strong>Dashboard tour</strong> for a card-by-card walkthrough.',
        ),
        navStep(
            'clients',
            'clients',
            'Clients',
            'The heart of the platform — and the page you\'ll spend the most time on. This is where you <strong>add new clients</strong> (manually or by sending a self-service onboarding link), and it\'s the full register of every client your firm has onboarded. From here you can search, filter by risk or CDD status, raise escalations against a client, run periodic reviews, and offboard when a relationship ends.',
        ),
        navStep(
            'personnel',
            'personnel',
            'Personnel',
            'Your firm\'s staff register — position titles, AML roles (CO/SM/GB/Admin/Staff), due-diligence status, training compliance, and commenced dates. Add new staff and run personnel reviews from here. <em>Visible to management only.</em>',
        ),
        navStep(
            'training',
            'training',
            'Training',
            'AML training modules. Staff complete required courses here; managers can assign modules and track completion.',
        ),
        navStep(
            'documents',
            'documents',
            'Deeds',
            'For your onboarded clients you can easily order a range of Trust Deeds, companies and other documents.',
        ),
        navStep(
            'macquarie-tv',
            'macquarie-tv',
            'Macquarie TV',
            'Macquarie\'s complete range of video content to earn CPE/CPD credits, included with your Macquarie AML subscription.',
        ),
        navStep(
            'escalations',
            'escalations',
            'Escalations',
            'Every flagged compliance issue across the firm — PEP/sanctions hits, ID discrepancies, suspicious matters. Triage, investigate, and close them out from here.',
        ),
        navStep(
            'reporting',
            'reporting',
            'Reporting',
            'Regulatory submissions — AUSTRAC reports, Suspicious Matter Reports (SMRs), threshold transactions. Tracks deadlines and submission state. <em>Visible to management only.</em>',
        ),
        navStep(
            'audit-export',
            'audit-export',
            'Service Audit',
            'A read-only register of every designated service your firm has provided, sliced for AUSTRAC audit and internal review. Filter by client, date, or service type, and export to spreadsheet. <em>Visible to management only.</em>',
        ),
        navStep(
            'compliance-log',
            'compliance-log',
            'Compliance Log',
            'A timestamped audit trail of every compliance-related action taken in the platform. Useful for evidence at audit time. <em>Visible to management only.</em>',
        ),
        navStep(
            'program',
            'program',
            'Program',
            'Your AML/CTF program documentation — risk assessment, policies, procedures, and the program review/evaluation cycle. <em>Visible to management only.</em>',
        ),
        navStep(
            'settings',
            'settings',
            'Settings / Admin',
            'Practice-level configuration — AUSTRAC enrolment, sanctions list sources, integration credentials, system preferences. <em>Compliance Officer / Admin only.</em>',
        ),
        navStep(
            'help',
            'help-walkthroughs',
            'Help',
            'You\'re looking at it from here. The Help page lists every available walkthrough — replay this one, the dashboard tour, the Add Client tour, or any future ones we build.',
        ),
        {
            id: 'finish',
            title: 'That\'s the sidebar',
            text: `Each module has its own tours and help — over time, you\'ll see more
                walkthroughs appear on the Help page covering specific tasks within each
                module.`,
            buttons: [BACK, DONE],
        },
    ],
};
